// React
import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';

// Redux
import { connect } from 'react-redux';
import { getSetPointsForDevice } from '_redux/iot/selectors';

// Components
import ShowHideGroup from '_components/device_settings/ShowHideGroup.component';
import { getUserDeviceCardSettingsById } from '_redux/dashboard/selectors';

// UI Framework
import { Text, View } from 'native-base';
import { ScrollView } from 'react-native';

import { styles } from '_components/device_settings/VitalsSettings.style';

export const defaultVitalsSettings = {
  oilPressure: { show: true, order: 0 },
  coolantTemp: { show: true, order: 1 },
  battery: { show: true, order: 2 },
  fuelLevel: { show: true, order: 3 },
  fuelRate: { show: true, order: 4 },
  flowRate: { show: true, order: 5 },
  totalFlow: { show: true, order: 6 },
  load: { show: true, order: 7 },
  defLevel: { show: false, order: 8 },
  soot: { show: false, order: 9 },
  hours: { show: false, order: 10 },
};

const THUMB_MIN_HEIGHT = 24;
const TRACK_WIDTH = 6;

class VitalsSettings extends Component {

  constructor(props) {
    super(props);

    const { deviceSettings } = props;
    const { vitalsSettings } = deviceSettings;
    const mergedVitals = { ...defaultVitalsSettings, ...(vitalsSettings || {}) };

    this.state = {
      currentVitals: mergedVitals,
      scrollTop: 0,
      containerHeight: 0,
      contentHeight: 0,
    };

    this.scrollRef = createRef();
    this._show = this._show.bind(this);
    this._hide = this._hide.bind(this);
    this._handleScroll = this._handleScroll.bind(this);
    this._handleLayout = this._handleLayout.bind(this);
    this._handleContentSizeChange = this._handleContentSizeChange.bind(this);
  }

  _handleScroll(e) {
    this.setState({ scrollTop: e.nativeEvent.contentOffset.y });
  }

  _handleLayout(e) {
    this.setState({ containerHeight: e.nativeEvent.layout.height });
  }

  _handleContentSizeChange(_, height) {
    this.setState({ contentHeight: height });
  }

  _modifyShowHide(vital, show) {
    const { settingsChanged, setError } = this.props;

    const newVitalsSettings = { ...this.state.currentVitals, ...{ [vital.id]: { order: vital.order, show: show } } };
    const numShow = Object.entries(newVitalsSettings).reduce((prevVal, curVal) => prevVal + (curVal[1].show ? 1 : 0), 0);

    if (numShow <= 8) {
      settingsChanged({ vitalsSettings: newVitalsSettings });
      setError(false, '');
    } else {
      setError(true, 'Only 8 vitals may be selected');
    }
    this.setState({ currentVitals: newVitalsSettings });
  }

  _show(vital) {
    this._modifyShowHide(vital, true);
  }

  _hide(vital) {
    this._modifyShowHide(vital, false);
  }

  _getThumbMetrics() {
    const { scrollTop, containerHeight, contentHeight } = this.state;
    if (!contentHeight || contentHeight <= containerHeight) {
      return { thumbHeight: 0, thumbTop: 0, showThumb: false };
    }
    const ratio = containerHeight / contentHeight;
    const thumbHeight = Math.max(THUMB_MIN_HEIGHT, containerHeight * ratio);
    const scrollable = contentHeight - containerHeight;
    const trackScrollable = containerHeight - thumbHeight;
    const thumbTop = scrollable > 0 ? (scrollTop / scrollable) * trackScrollable : 0;
    return { thumbHeight, thumbTop, showThumb: true };
  }

  render() {
    const { currentVitals } = this.state;

    const vitalsMap = [
      { id: 'oilPressure', label: 'OIL PRESSURE' },
      { id: 'coolantTemp', label: 'COOLANT TEMP' },
      { id: 'battery', label: 'BATTERY' },
      { id: 'fuelLevel', label: 'FUEL LEVEL' },
      { id: 'fuelRate', label: 'FUEL RATE' },
      { id: 'flowRate', label: 'FLOW RATE' },
      { id: 'totalFlow', label: 'TOTAL FLOW' },
      { id: 'load', label: 'LOAD' },
      { id: 'defLevel', label: 'DEF LEVEL' },
      { id: 'soot', label: 'SOOT' },
      { id: 'hours', label: 'HOURS' },
    ];

    const sortedVitals = Object.entries(currentVitals).sort((a, b) => a[1].order - b[1].order);

    const vitalsComponents = [];
    sortedVitals.forEach((vital) => {
      const mapEntry = vitalsMap.filter(item => item.id === vital[0])[0];
      if (!mapEntry) return;
      vitalsComponents.push(
        <View key={vital[0]} style={[styles.vitalsRow]}>
          <Text style={[styles.vitalsLabel]}>{mapEntry.label}</Text>
          <ShowHideGroup
            onShow={() => this._show({ id: vital[0], order: vital[1].order })}
            onHide={() => this._hide({ id: vital[0], order: vital[1].order })}
            show={vital[1].show}
          />
        </View>
      );
    });

    const { thumbHeight, thumbTop, showThumb } = this._getThumbMetrics();

    return (
      <View style={scrollbarStyles.wrapper}>
        <ScrollView
          ref={this.scrollRef}
          style={[styles.vitalsContainer, scrollbarStyles.scrollView]}
          showsVerticalScrollIndicator={false}
          onScroll={this._handleScroll}
          onLayout={this._handleLayout}
          onContentSizeChange={this._handleContentSizeChange}
          scrollEventThrottle={16}
        >
          {vitalsComponents}
        </ScrollView>

        {showThumb && (
          <View style={scrollbarStyles.track}>
            <View style={[scrollbarStyles.thumb, { height: thumbHeight, top: thumbTop }]} />
          </View>
        )}
      </View>
    );
  }
}

const scrollbarStyles = {
  wrapper: {
    flex: 1,
    flexDirection: 'row',
    width: '100%',
  },
  scrollView: {
    flex: 1,
  },
  track: {
    width: TRACK_WIDTH,
    backgroundColor: '#2a2a2a',
    borderRadius: 3,
    marginVertical: 2,
    position: 'relative',
  },
  thumb: {
    position: 'absolute',
    width: TRACK_WIDTH,
    backgroundColor: '#888',
    borderRadius: 3,
    left: 0,
  },
};

const mapStateToProps = (state, ownProps) => {
  return {
    deviceSettings: getUserDeviceCardSettingsById(state, ownProps.deviceId),
  };
};

VitalsSettings.propTypes = {
  deviceId: PropTypes.string.isRequired,
  settingsChanged: PropTypes.func.isRequired,
  deviceSettings: PropTypes.any.isRequired,
};

export default connect(mapStateToProps, null)(VitalsSettings);
